<template>
  <div>
    <div class="page-toolbar">
      <div class="page-title">商品管理</div>
      <el-button icon="el-icon-refresh" @click="loadProducts">刷新</el-button>
    </div>

    <el-card class="page-card">
      <div class="search-toolbar">
        <el-input
          v-model.trim="filters.id"
          clearable
          placeholder="按商品ID查询"
          style="width: 220px;"
          @keyup.enter.native="loadProducts"
        />
        <el-input
          v-model.trim="filters.name"
          clearable
          placeholder="按商品名称查询"
          style="width: 220px;"
          @keyup.enter.native="loadProducts"
        />
        <el-input
          v-model.trim="filters.phone"
          clearable
          placeholder="按手机号查询"
          style="width: 220px;"
          @keyup.enter.native="loadProducts"
        />
        <el-button type="primary" icon="el-icon-search" @click="loadProducts">查询</el-button>
        <el-button @click="resetFilters">重置</el-button>
      </div>

      <el-table :data="products" v-loading="loading" stripe>
        <el-table-column type="index" width="56" label="#" />
        <el-table-column label="商品ID" min-width="220" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span :title="row.id || ''">{{ formatDisplayProductId(row.id) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="大米标题" min-width="170" />
        <el-table-column label="产品介绍" min-width="200">
          <template slot-scope="{ row }">
            <span class="text-ellipsis">{{ row.description || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="category" label="品种" width="100" />
        <el-table-column label="规格" min-width="130">
          <template slot-scope="{ row }">
            {{ resolveSpecification(row) }}
          </template>
        </el-table-column>
        <el-table-column prop="productionDate" label="生产日期" width="120" />
        <el-table-column label="审批状态" width="120">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="auditTagType(row)">
              {{ auditLabel(row) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="上架状态" width="100">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.onShelf ? 'success' : 'info'">
              {{ row.onShelf ? '上架中' : '已下架' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="发布时间" width="170" />
        <el-table-column label="操作" width="280" fixed="right">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="showDetail(row)">查看</el-button>

            <el-button
              v-if="isPending(row) || isRejected(row)"
              size="mini"
              type="success"
              @click="approveProduct(row)"
            >
              审核通过
            </el-button>

            <el-button
              v-if="isPending(row) || isApproved(row)"
              size="mini"
              type="danger"
              @click="rejectProduct(row)"
            >
              驳回
            </el-button>

            <el-button
              v-if="isApproved(row)"
              size="mini"
              :type="row.onShelf ? 'warning' : 'primary'"
              @click="toggleShelf(row)"
            >
              {{ row.onShelf ? '下架' : '上架' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog title="商品详情" :visible.sync="detailVisible" width="780px">
      <div v-if="current">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="商品ID">{{ formatDisplayProductId(current.id) }}</el-descriptions-item>
          <el-descriptions-item label="大米标题">{{ current.name || '-' }}</el-descriptions-item>
          <el-descriptions-item label="产品介绍" :span="2">{{ current.description || '-' }}</el-descriptions-item>
          <el-descriptions-item label="品种">{{ current.category || '-' }}</el-descriptions-item>
          <el-descriptions-item label="规格">{{ resolveSpecification(current) }}</el-descriptions-item>
          <el-descriptions-item label="货物所在地">{{ current.region || '-' }}</el-descriptions-item>
          <el-descriptions-item label="大米生产地址">{{ current.productionPlace || '-' }}</el-descriptions-item>
          <el-descriptions-item label="生产日期">{{ current.productionDate || '-' }}</el-descriptions-item>
          <el-descriptions-item label="售价/吨">{{ formatPricePerTon(current.price) }}</el-descriptions-item>
          <el-descriptions-item label="库存数量">{{ current.stock }}</el-descriptions-item>
          <el-descriptions-item label="起购数">{{ formatTons(current.minOrderTons) }}</el-descriptions-item>
          <el-descriptions-item label="发布方">{{ current.supplierName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="审批状态">{{ auditLabel(current) }}</el-descriptions-item>
          <el-descriptions-item label="驳回/审批说明" :span="2">{{ current.auditComment || '-' }}</el-descriptions-item>
          <el-descriptions-item label="发布时间">{{ current.createTime || '-' }}</el-descriptions-item>
          <el-descriptions-item label="展示图片" :span="2">
            <div v-if="resolveDetailImages(current).length" class="detail-images">
              <el-image
                v-for="(image, index) in resolveDetailImages(current)"
                :key="`${index}-${image}`"
                :src="image"
                :preview-src-list="resolveDetailImages(current)"
                fit="cover"
                class="detail-image-item"
              />
            </div>
            <span v-else>-</span>
          </el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { fetchProducts, reviewProduct, setProductShelf } from '../../api/admin'

const AUDIT_STATUS = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED'
}

export default {
  name: 'ProductListView',
  data() {
    return {
      loading: false,
      products: [],
      detailVisible: false,
      current: null,
      filters: {
        id: '',
        name: '',
        phone: ''
      }
    }
  },
  created() {
    this.loadProducts()
  },
  methods: {
    async loadProducts() {
      this.loading = true
      try {
        const params = {}
        const id = String(this.filters.id || '').trim()
        const name = String(this.filters.name || '').trim()
        const phone = String(this.filters.phone || '').trim()
        if (id) params.id = id
        if (name) params.name = name
        if (phone) params.phone = phone
        const products = await fetchProducts(params)
        this.products = Array.isArray(products) ? products : []
      } catch (error) {
        this.$message.error(error.message || '商品列表获取失败')
      } finally {
        this.loading = false
      }
    },
    resetFilters() {
      this.filters = { id: '', name: '', phone: '' }
      this.loadProducts()
    },
    showDetail(row) {
      this.current = row
      this.detailVisible = true
    },
    formatDisplayProductId(value) {
      const raw = String(value || '').trim()
      if (!raw) return '-'
      if (/^\d+$/.test(raw)) return raw
      const matched = raw.match(/(?:^|[_-])(\d+)$/)
      return matched ? matched[1] : raw
    },
    normalizeAuditStatus(row = {}) {
      const raw = String((row && row.auditStatus) || '').trim().toUpperCase()
      if (!raw) {
        return AUDIT_STATUS.PENDING
      }
      if (raw === AUDIT_STATUS.PENDING || raw === AUDIT_STATUS.REJECTED || raw === AUDIT_STATUS.APPROVED) {
        return raw
      }
      return AUDIT_STATUS.PENDING
    },
    isPending(row) {
      return this.normalizeAuditStatus(row) === AUDIT_STATUS.PENDING
    },
    isApproved(row) {
      return this.normalizeAuditStatus(row) === AUDIT_STATUS.APPROVED
    },
    isRejected(row) {
      return this.normalizeAuditStatus(row) === AUDIT_STATUS.REJECTED
    },
    auditLabel(row) {
      const status = this.normalizeAuditStatus(row)
      if (status === AUDIT_STATUS.PENDING) return '待审批'
      if (status === AUDIT_STATUS.REJECTED) return '已驳回'
      return '已通过'
    },
    auditTagType(row) {
      const status = this.normalizeAuditStatus(row)
      if (status === AUDIT_STATUS.PENDING) return 'warning'
      if (status === AUDIT_STATUS.REJECTED) return 'danger'
      return 'success'
    },
    async approveProduct(row) {
      try {
        const updated = await reviewProduct(row.id, true, '')
        this.$message.success('审批通过成功')
        this.applyUpdatedProduct(updated)
      } catch (error) {
        this.$message.error(error.message || '审批失败')
      }
    },
    rejectProduct(row) {
      this.$prompt('请输入驳回原因（可选）', '驳回商品', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputPlaceholder: '例如：图片不清晰、信息不完整'
      }).then(async ({ value }) => {
        try {
          const updated = await reviewProduct(row.id, false, value || '')
          this.$message.success('已驳回')
          this.applyUpdatedProduct(updated)
        } catch (error) {
          this.$message.error(error.message || '驳回失败')
        }
      }).catch(() => {})
    },
    toggleShelf(row) {
      const nextOnShelf = !row.onShelf
      const actionText = nextOnShelf ? '上架' : '下架'
      this.$confirm(`确认${actionText}该商品吗？`, '提示', { type: 'warning' })
        .then(async () => {
          const updated = await setProductShelf(row.id, nextOnShelf)
          this.$message.success(`${actionText}成功`)
          this.applyUpdatedProduct(updated)
        })
        .catch(() => {})
    },
    applyUpdatedProduct(updated = {}) {
      this.products = this.products.map(item => (item.id === updated.id ? { ...item, ...updated } : item))
      if (this.current && this.current.id === updated.id) {
        this.current = { ...this.current, ...updated }
      }
    },
    resolveSpecification(row = {}) {
      const specs = Array.isArray(row.specifications) ? row.specifications : []
      const matched = specs.find(item => {
        const name = String((item && item.name) || '').trim()
        return name === '规格' || name === '包装规格'
      })
      const value = String((matched && matched.value) || '').trim()
      return value || '-'
    },
    formatTons(value) {
      const num = Number(value)
      if (!Number.isFinite(num) || num <= 0) return '-'
      return `${this.trimNumber(num)} 吨`
    },
    formatPricePerTon(value) {
      const num = Number(value)
      if (!Number.isFinite(num) || num <= 0) return '-'
      return `¥${this.trimNumber(num)}/吨`
    },
    trimNumber(value) {
      const num = Number(value)
      if (!Number.isFinite(num)) return '-'
      return Number.isInteger(num)
        ? `${num}`
        : num.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1')
    },
    resolveDetailImages(row = {}) {
      const images = Array.isArray(row.images) ? row.images.filter(Boolean).slice(0, 2) : []
      if (images.length > 0) return images
      return row.image ? [row.image] : []
    }
  }
}
</script>

<style scoped>
.search-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.text-ellipsis {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-images {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.detail-image-item {
  width: 100%;
  height: 110px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
}
</style>
